<template>
  <div class="m-page">
    <div class="m-back">
      <el-button link type="primary" @click="goBackNav">← {{ isTaskBranchMode ? '返回任务' : '返回列表' }}</el-button>
      <el-button v-if="!isTaskBranchMode" link type="primary" @click="goRun">去跑码 →</el-button>
    </div>

    <div v-loading="loading" class="m-body">
      <template v-if="order">
        <section class="m-hero">
          <div class="m-hero__no">{{ order.dispatch_order_no }}</div>
          <div class="m-hero__sub">
            <span>{{ headerCustomer }}</span>
            <el-tag size="small" :type="statusTag(order.status)">{{ statusText(order.status) }}</el-tag>
          </div>
          <p class="m-intro">
            <template v-if="isTaskBranchMode">
              本页仅处理<strong>当前这一支</strong>的称重与外尺寸；填写保存后请点「完成本支任务」，不会一次关掉其它支待办。
            </template>
            <template v-else>
              每条分码（每支）在保存跑码后由系统生成<strong>包装编号</strong>：派工单号 + 本单内序列号。请按编号对本支称重、量外包装尺寸。
            </template>
          </p>
          <p v-if="packagingOperatorName" class="m-pack-operator">
            包装填写已锁定：<strong>{{ packagingOperatorName }}</strong>
            <span class="muted">（保存后自动锁定，业务可解除）</span>
          </p>
          <p v-if="order.packaging_last_saved_by_name" class="m-pack-saved muted">
            最近保存：{{ order.packaging_last_saved_by_name }}
            <template v-if="order.packaging_last_saved_at"> · {{ formatDt(order.packaging_last_saved_at) }}</template>
          </p>
        </section>

        <section class="m-section">
          <h3 class="m-h3">包装要求摘要</h3>
          <div class="m-kv"><span class="k">袋型</span><span class="v">{{ packagingTypeLabel }}</span></div>
          <div class="m-kv"><span class="k">需称重</span><span class="v">{{ needWeighAny ? '是（按支）' : '否' }}</span></div>
          <div class="m-kv"><span class="k">需量体积</span><span class="v">{{ needVolumeAny ? '是（按支）' : '否' }}</span></div>
        </section>

        <section v-if="!displayPackLines.length && !loading" class="m-section">
          <el-alert type="warning" show-icon :closable="false" title="暂无分码数据" description="请先在「跑码」页为每条订单明细分卷/分码并保存后，再回到本页按支填写称重与尺寸。" />
        </section>

        <section v-else class="m-section">
          <h3 class="m-h3">{{ isTaskBranchMode ? '本支实测' : `按支实测（${displayPackLines.length} 支）` }}</h3>
          <p v-if="fromPackagingTask && canEditMeasures" class="m-pack-task-hint muted">
            任务待办模式：现场已提交完成仍可补填本支称重/外尺寸，保存后请点「完成本支任务」。
          </p>
          <p v-else-if="!needWeighAny && !needVolumeAny && !fromPackagingTask" class="muted">本单未勾选称重或量体积，无需填写。</p>

          <el-alert
            v-if="!canSavePackaging && order"
            type="warning"
            show-icon
            :closable="false"
            class="m-pack-alert"
            :title="saveBlockedTitle"
            :description="saveBlockedDesc"
          />

          <div v-for="row in displayPackLines" :key="row.branch_record_id" class="m-pack-card">
            <div class="m-pack-card__no">{{ row.branch_pack_code || '（保存分码后生成编号）' }}</div>
            <div class="m-pack-card__meta muted">
              <template v-if="row.customerName">客户 {{ row.customerName }} · </template>
              订单 {{ row.order_no || '—' }}
              <template v-if="row.client_purchase_order"> · 采购单 {{ row.client_purchase_order }}</template>
            </div>
            <div v-if="row.internalMaterialName" class="m-pack-card__meta">{{ row.internalMaterialName }}</div>
            <div v-if="row.internalColorLine" class="m-pack-card__meta muted">{{ row.internalColorLine }}</div>
            <div class="m-pack-card__meta">标签 {{ row.displayLabel }} · 数量 {{ fmtNumMaybe1(row.quantity) }} {{ row.unit || '' }}</div>
            <div v-if="row.packagingMeasuredByName" class="m-pack-card__meta muted">
              录入 {{ row.packagingMeasuredByName }}
              <template v-if="row.packagingMeasuredAt"> · {{ formatDt(row.packagingMeasuredAt) }}</template>
            </div>

            <template v-if="canEditMeasures && (row.needWeigh || (fromPackagingTask && needWeighAny))">
              <div v-if="row.needWeigh || (fromPackagingTask && needWeighAny)" class="m-pack-field">
                <label class="m-lbl">本支重量 (kg)</label>
                <el-input
                  v-model="row.weightStr"
                  inputmode="decimal"
                  autocomplete="off"
                  placeholder="如 12.5"
                  class="m-fw"
                  clearable
                />
              </div>
              <div v-if="row.needVolume || (fromPackagingTask && needVolumeAny)" class="m-pack-dims">
                <div class="m-pack-field">
                  <label class="m-lbl">长 (cm)</label>
                  <el-input v-model="row.lenStr" inputmode="numeric" autocomplete="off" placeholder="整数" class="m-fw" clearable />
                </div>
                <div class="m-pack-field">
                  <label class="m-lbl">宽 (cm)</label>
                  <el-input v-model="row.widStr" inputmode="numeric" autocomplete="off" placeholder="整数" class="m-fw" clearable />
                </div>
                <div class="m-pack-field">
                  <label class="m-lbl">高 (cm)</label>
                  <el-input v-model="row.hgtStr" inputmode="numeric" autocomplete="off" placeholder="整数" class="m-fw" clearable />
                </div>
              </div>
            </template>

            <template v-else-if="!canEditMeasures && (row.needWeigh || row.needVolume)">
              <div v-if="row.needWeigh" class="m-kv sm">
                <span class="k">重量 kg</span><span class="v">{{ row.weightStr || '—' }}</span>
              </div>
              <div v-if="row.needVolume" class="m-kv sm">
                <span class="k">外尺寸 cm</span><span class="v">{{ dimsLine(row) }}</span>
              </div>
            </template>
          </div>

          <el-button
            v-if="canEditMeasures && (needWeighAny || needVolumeAny) && !isTaskBranchMode"
            type="primary"
            block
            class="m-save-all"
            :loading="saving"
            :disabled="!canSavePackaging || !displayPackLines.length"
            @click="saveAll"
          >
            保存全部支数实测
          </el-button>

          <el-button
            v-if="canSubmitAudit && (needWeighAny || needVolumeAny) && !isTaskBranchMode"
            type="warning"
            block
            plain
            class="m-save-all"
            :loading="submittingAudit"
            @click="submitPackagingAudit"
          >
            提交包装核销
          </el-button>
          <p v-if="packagingAuditLabel && !isTaskBranchMode" class="muted m-readonly-tip">
            包装核销：{{ packagingAuditLabel }}
          </p>

          <template v-if="isTaskBranchMode && canEditMeasures && (needWeighAny || needVolumeAny)">
            <el-button
              type="primary"
              block
              class="m-save-all"
              :loading="saving"
              :disabled="!canSavePackaging || !displayPackLines.length"
              @click="saveBranch"
            >
              保存本支实测
            </el-button>
            <el-button
              type="success"
              block
              plain
              class="m-save-all"
              :loading="completingTask"
              :disabled="!taskEventId || !canCompleteBranchTask"
              @click="completeBranchTask"
            >
              完成本支任务
            </el-button>
            <p v-if="!canCompleteBranchTask" class="muted m-readonly-tip">请先保存并填齐本支称重/外尺寸后再完成任务。</p>
          </template>
          <p v-if="!canEditMeasures" class="muted m-readonly-tip">{{ readonlyTip }}</p>
        </section>
      </template>

      <section v-else-if="!loading" class="m-section">
        <el-empty description="无法加载派工单详情" />
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onActivated } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getDispatchOrder, saveDispatchBranchPackMeasures, submitPackagingAuditDispatchOrder } from '@/api/dispatchOrders'
import { completeTaskEvent } from '@/api/taskEvents'
import { useUserStore } from '@/store/user'
import { buildPackLinesFromDispatchOrder, fmtNumMaybe1 } from '@/utils/dispatchPackagingMeasures'
import { branchMeasuresFilled } from '@/utils/taskPackaging'
import { navigateTaskReturnIfPresent } from '@/utils/taskReturnNavigation'
import {
  canOperateDispatchPackaging,
  canEditDispatchPackagingMeasures,
  canSubmitPackagingAudit,
  isDispatchPackagingReadonly,
  packagingAuditStatusLabel,
  packagingOperatorDisplay,
  packLinesHaveIncompleteMeasures
} from '@/utils/dispatchMobilePermissions'

import { useMobileWorkbench } from '@/composables/useMobileWorkbench'

defineOptions({ name: 'MobileDispatchPackaging' })

const { mPath } = useMobileWorkbench()

const PACKAGING_ROUTE = 'MobileDispatchPackaging'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const loading = ref(true)
const saving = ref(false)
const submittingAudit = ref(false)
const completingTask = ref(false)
const order = ref(null)
const packLines = ref([])
const loadedCacheKey = ref('')

const taskBranchId = computed(() => {
  const q = route.query?.branch_record_id
  const n = Number(q)
  return Number.isFinite(n) && n > 0 ? n : null
})

const taskEventId = computed(() => {
  const q = route.query?.task_event_id
  const n = Number(q)
  return Number.isFinite(n) && n > 0 ? n : null
})

const isTaskBranchMode = computed(() => taskBranchId.value != null)

const fromPackagingTask = computed(
  () =>
    isTaskBranchMode.value &&
    (taskEventId.value != null || String(route.query?.from || '').trim() === 'tasks')
)

const packagingEditOptions = computed(() => ({
  fromPackagingTask: fromPackagingTask.value,
  packagingMeasuresIncomplete: !!order.value?.packaging_measures_incomplete,
  taskBranchId: taskBranchId.value
}))

const displayPackLines = computed(() => {
  if (!isTaskBranchMode.value) return packLines.value
  return packLines.value.filter((row) => Number(row.branch_record_id) === taskBranchId.value)
})

function isPackagingRoute() {
  return route.name === PACKAGING_ROUTE
}

const canOperatePackaging = computed(() =>
  canOperateDispatchPackaging(userStore.user, order.value)
)

const packagingOperatorName = computed(() => packagingOperatorDisplay(order.value))

const readonly = computed(() =>
  isDispatchPackagingReadonly(order.value, packLines.value, packagingEditOptions.value)
)

const canEditMeasures = computed(() =>
  canEditDispatchPackagingMeasures(
    userStore.user,
    order.value,
    packLines.value,
    packagingEditOptions.value
  )
)

const canSavePackaging = computed(() => canEditMeasures.value)

const canSubmitAudit = computed(() => {
  if (isTaskBranchMode.value) return false
  return canSubmitPackagingAudit(order.value, packLines.value)
})

const packagingAuditLabel = computed(() =>
  packagingAuditStatusLabel(order.value?.packaging_audit_status)
)

const canCompleteBranchTask = computed(() => {
  if (!isTaskBranchMode.value || !displayPackLines.value.length) return false
  return branchMeasuresFilled(displayPackLines.value[0])
})

const readonlyTip = computed(() => {
  if (order.value?.status === 'cancelled') return '派工单已取消，仅供查看。'
  const pa = (order.value?.packaging_audit_status || 'na').toLowerCase()
  if (pa === 'pending') return '包装资料已提交业务核销，待审核期间不能修改。'
  if (pa === 'approved') return '包装核销已通过，包装实测已锁定。'
  if (pa === 'rejected') return '包装核销已退回，请修改后重新提交包装核销。'
  const lines = isTaskBranchMode.value ? displayPackLines.value : packLines.value
  if (packLinesHaveIncompleteMeasures(lines)) {
    return '本支仍缺称重/外尺寸，请补填后点「保存本支实测」，再点「完成本支任务」。'
  }
  const op = packagingOperatorName.value
  if (op && !canOperatePackaging.value) {
    return `本单包装资料已由「${op}」保存锁定，您无权修改；如需调整请联系业务解除锁定。`
  }
  return '当前不可编辑。'
})

const saveBlockedTitle = computed(() => {
  if (!canOperatePackaging.value) return '已由他人锁定'
  if (canEditMeasures.value) return ''
  const pa = (order.value?.packaging_audit_status || 'na').toLowerCase()
  if (pa === 'pending') return '待包装核销'
  if (pa === 'approved') return '包装已核销'
  return '暂不可保存'
})

const saveBlockedDesc = computed(() => readonlyTip.value)

const items = computed(() => {
  const raw = order.value?.items
  return Array.isArray(raw) ? raw : []
})

const headerCustomer = computed(() => {
  const fi = items.value.find((i) => i.customer_name)
  return fi?.customer_name || '—'
})

const needWeighAny = computed(() => {
  const o = order.value
  if (!o) return false
  if (o.packaging_require_weigh) return true
  return items.value.some((it) => it.packaging_require_weigh)
})

const needVolumeAny = computed(() => {
  const o = order.value
  if (!o) return false
  if (o.packaging_require_volume) return true
  return items.value.some((it) => it.packaging_require_volume)
})

const packagingTypeLabel = computed(() => {
  const o = order.value
  const t = o?.packaging_type
  if (t === 'transparent_bag') return '透明袋'
  if (t === 'woven_bag') return '蛇皮袋'
  return '未指定'
})

function statusText(s) {
  const map = { draft: '草稿', assigned: '已指派', in_progress: '进行中', completed: '已完成', cancelled: '已取消' }
  return map[s] || s
}

function statusTag(s) {
  const map = { draft: 'info', assigned: 'primary', in_progress: 'warning', completed: 'success', cancelled: 'danger' }
  return map[s] || ''
}

function formatDt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16).replace('T', ' ')
  return d.toLocaleString('zh-CN', { hour12: false })
}

function dimsLine(row) {
  const a = [row.lenStr, row.widStr, row.hgtStr].filter((x) => x != null && String(x).trim() !== '')
  return a.length ? a.join(' × ') : '—'
}

function parseFloatOrNull(s) {
  if (s == null || String(s).trim() === '') return null
  const n = parseFloat(String(s).replace(',', '.'))
  if (Number.isNaN(n)) return null
  return Math.round(n * 10) / 10
}

function parseIntOrNull(s) {
  if (s == null || String(s).trim() === '') return null
  const n = parseInt(String(s).trim(), 10)
  return Number.isNaN(n) ? null : n
}

function packagingCacheKey() {
  const id = route.params.id
  const branch = route.query?.branch_record_id ?? ''
  const task = route.query?.task_event_id ?? ''
  return `${id}:${branch}:${task}`
}

async function load(options = {}) {
  const { force = false, silent = false } = options
  if (!isPackagingRoute()) return
  const id = Number(route.params.id)
  if (!Number.isFinite(id) || id <= 0) return
  const cacheKey = packagingCacheKey()
  if (!force && cacheKey === loadedCacheKey.value && order.value) return
  if (!silent) loading.value = true
  try {
    const res = await getDispatchOrder(id, {
      params: {
        require_audit_approved: 'true',
        packaging_context: 'true'
      }
    })
    if (res.success && res.data) {
      order.value = res.data
      packLines.value = buildPackLinesFromDispatchOrder(res.data)
      loadedCacheKey.value = cacheKey
      if (isTaskBranchMode.value && !displayPackLines.value.length) {
        ElMessage.warning('未找到该包装编号对应的分码，请返回任务列表刷新')
      }
    } else {
      ElMessage.error(res.message || '加载失败')
    }
  } catch (e) {
    const msg = e.response?.data?.message || e.message || '加载失败'
    ElMessage.error(msg)
  } finally {
    if (!silent) loading.value = false
  }
}

async function saveBranchMeasures(rows) {
  if (!rows?.length) return false
  const id = Number(route.params.id)
  const measures = []
  const missingId = []
  for (const row of rows) {
    if (!row.needWeigh && !row.needVolume) continue
    if (!row.branch_record_id) {
      missingId.push(row.branch_pack_code || row.displayLabel || '?')
      continue
    }
    const m = { branch_record_id: row.branch_record_id }
    if (row.needWeigh) m.weight_kg = parseFloatOrNull(row.weightStr)
    if (row.needVolume) {
      m.length_cm = parseIntOrNull(row.lenStr)
      m.width_cm = parseIntOrNull(row.widStr)
      m.height_cm = parseIntOrNull(row.hgtStr)
    }
    measures.push(m)
  }
  if (missingId.length) {
    ElMessage.warning('该分码尚未保存跑码，请先到「跑码」页保存分码后再填写称重')
    return false
  }
  if (!measures.length) {
    ElMessage.info('本支未要求称重或量体积，无需保存')
    return false
  }
  saving.value = true
  try {
    const res = await saveDispatchBranchPackMeasures(id, { measures })
    if (res.success) {
      ElMessage.success(res.message || '已保存')
      await load({ force: true, silent: true })
      return true
    }
    ElMessage.error(res.message || '保存失败')
    return false
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
    return false
  } finally {
    saving.value = false
  }
}

async function saveAll() {
  if (!canOperatePackaging.value) {
    ElMessage.warning(readonlyTip.value || '本单已由他人锁定')
    return
  }
  if (readonly.value) {
    ElMessage.warning(readonlyTip.value)
    return
  }
  await saveBranchMeasures(packLines.value)
}

async function submitPackagingAudit() {
  if (!canSubmitAudit.value) {
    ElMessage.warning('请先保存并填齐全部支数称重/外尺寸后再提交')
    return
  }
  const id = Number(route.params.id)
  try {
    await ElMessageBox.confirm(
      '提交后包装资料将进入业务核销，提交期间及通过后不能再修改。确认提交？',
      '提交包装核销',
      { type: 'warning', confirmButtonText: '提交', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  submittingAudit.value = true
  try {
    const res = await submitPackagingAuditDispatchOrder(id, {})
    if (res.success) {
      ElMessage.success(res.message || '已提交包装核销')
      await load({ force: true })
    } else {
      ElMessage.error(res.message || '提交失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '提交失败')
  } finally {
    submittingAudit.value = false
  }
}

async function saveBranch() {
  if (!canOperatePackaging.value) {
    ElMessage.warning(readonlyTip.value || '本单已由他人锁定')
    return
  }
  if (readonly.value) {
    ElMessage.warning(readonlyTip.value)
    return
  }
  await saveBranchMeasures(displayPackLines.value)
}

async function completeBranchTask() {
  if (!taskEventId.value) {
    ElMessage.warning('缺少任务编号，请从任务中心重新进入')
    return
  }
  if (!canCompleteBranchTask.value) {
    ElMessage.warning('请先保存并填齐本支称重/外尺寸')
    return
  }
  completingTask.value = true
  try {
    await completeTaskEvent(taskEventId.value)
    ElMessage.success('本支任务已完成')
    if (navigateTaskReturnIfPresent(router, route)) return
    router.push(mPath('task-center'))
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '完成任务失败')
  } finally {
    completingTask.value = false
  }
}

function goBackNav() {
  if (navigateTaskReturnIfPresent(router, route)) return
  goList()
}

function goList() {
  router.push(mPath('dispatch'))
}

function goRun() {
  router.push(mPath(`dispatch/${route.params.id}`))
}

onMounted(() => {
  userStore.initFromStorage()
  load({ force: true })
})

onActivated(() => {
  if (!isPackagingRoute()) return
  if (packagingCacheKey() !== loadedCacheKey.value) {
    load({ force: true })
  }
})
</script>

<style scoped>
.m-page {
  max-width: 720px;
  margin: 0 auto;
}

.m-back {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0 12px;
}

.m-body {
  padding-bottom: 24px;
}

.m-hero__no {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.m-hero__sub {
  margin-top: 6px;
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.m-intro {
  margin-top: 12px;
  font-size: 13px;
  color: #475569;
  line-height: 1.55;
}

.m-section {
  margin-top: 18px;
  padding: 0 4px;
}

.m-h3 {
  font-size: 16px;
  margin: 0 0 10px;
  color: #0f172a;
}

.m-kv {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  margin-bottom: 8px;
}

.m-kv.sm {
  font-size: 13px;
}

.m-kv .k {
  color: #64748b;
  flex-shrink: 0;
}

.m-kv .v {
  text-align: right;
}

.m-pack-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #fff;
}

.m-pack-card__no {
  font-family: ui-monospace, monospace;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  word-break: break-all;
  line-height: 1.4;
}

.m-pack-card__meta {
  font-size: 12px;
  margin-top: 6px;
  line-height: 1.4;
}

.muted {
  color: #94a3b8;
}

.m-pack-field {
  margin-top: 10px;
}

.m-lbl {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.m-fw {
  width: 100%;
}

/* iOS：字号 ≥16px 避免聚焦时整页放大 */
.m-fw :deep(.el-input__inner) {
  font-size: 16px;
}

.m-pack-dims {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}

.m-save-all {
  margin-top: 16px;
}

.m-readonly-tip {
  margin-top: 14px;
}

.m-pack-alert {
  margin-bottom: 12px;
}

.m-pack-operator {
  margin-top: 10px;
  font-size: 13px;
  color: var(--el-color-primary);
}

.m-pack-saved {
  margin-top: 6px;
  font-size: 12px;
}

.m-section--access {
  margin-top: 12px;
}

.m-access-reason {
  margin: 8px 0 0;
  font-size: 13px;
}

.m-dialog-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.m-mt {
  margin-top: 10px;
}
</style>
